import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import BackLink from "@/components/BackLink";
import { Calendar, MapPin, Users, Mail, Phone, AlertTriangle, ExternalLink, Database, User } from "lucide-react";
import AndringsforslagButton from "@/components/AndringsforslagButton";
import SocialLinks from "@/components/SocialLinks";
import ShareWidget from "@/components/ShareWidget";
import GalleriStrip from "@/components/GalleriStrip";
import EntryFootnote from "@/components/EntryFootnote";
import ExpandableBeskrivning from "@/components/ExpandableBeskrivning";
import { usePageMeta, SITE } from "@/utils/usePageMeta";
import { formatEventDateRange, formatEventTimeRange } from "@/utils/eventDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['januari','februari','mars','april','maj','juni','juli','augusti','september','oktober','november','december'];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function buildEventJsonLd(event) {
  if (!event?.titel || !event?.datum) return null;
  // tid kan vara "18:00" eller en range som "10:00-15:00" / "10:00–15:00"
  // schema.org/Event kräver ISO-8601 startDate. Vi tar bara starttid.
  let startTime = null;
  if (event.tid) {
    const m = String(event.tid).match(/(\d{1,2}):(\d{2})/);
    if (m) startTime = `${m[1].padStart(2, "0")}:${m[2]}`;
  }
  const start = startTime ? `${event.datum}T${startTime}:00+01:00` : event.datum;
  const ld = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.titel,
    "startDate": start,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "description": event.kortbeskrivning || (event.beskrivning || "").slice(0, 300),
    "url": `${SITE}/evenemang/${event.id}`,
  };
  if (event.plats || event.adress) {
    ld.location = {
      "@type": "Place",
      "name": event.plats || event.adress,
      "address": event.adress || event.plats || "Backe, Sverige",
    };
  }
  if (event.arrangor) {
    ld.organizer = { "@type": "Organization", "name": event.arrangor };
  }
  if (event.galleri && event.galleri.length > 0) {
    ld.image = event.galleri.map((g) => g.startsWith("http") ? g : `${SITE}${g}`);
  }
  return ld;
}

export default function EvenemangDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/evenemang/${id}`)
      .then(r => { setEvent(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  usePageMeta(event ? {
    title: `${event.titel} – Evenemang | vidalv.live`,
    description: event.kortbeskrivning || (event.beskrivning || "").slice(0, 200) || `${event.titel} på ${event.plats || "vidalv.live"}`,
    path: `/evenemang/${id}`,
    image: event.galleri && event.galleri[0] ? (event.galleri[0].startsWith("http") ? event.galleri[0] : `${SITE}${event.galleri[0]}`) : undefined,
    jsonLd: buildEventJsonLd(event),
    jsonLdId: "ld-event",
  } : {});

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-14"><p className="text-muted-foreground">Laddar...</p></div>;
  if (!event) return <div className="max-w-3xl mx-auto px-4 py-14"><p className="text-muted-foreground">Evenemanget hittades inte.</p></div>;

  return (
    <div data-testid="evenemang-detail-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <BackLink to="/foreningar-evenemang" label="Föreningar & evenemang" />

      <span className="inline-block text-sm font-body font-medium px-2.5 py-1 rounded-sm mb-4 bg-bone-alt text-pine">
        Evenemang
      </span>

      <h1 className="font-heading text-2xl sm:text-4xl font-medium text-ink mb-6">{event.titel}</h1>

      <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 mb-6 space-y-4">
        <div className="flex flex-wrap gap-x-6 gap-y-3 text-base text-ink">
          {event.datum && (
            <span className="flex items-center gap-2">
              <Calendar size={18} className="text-pine" />
              {formatEventDateRange(event.datum, event.datum_slut)}{formatEventTimeRange(event.tid, event.tid_slut) ? `, ${formatEventTimeRange(event.tid, event.tid_slut)}` : ""}
            </span>
          )}
          {event.plats && (
            <span className="flex items-center gap-2">
              <MapPin size={18} className="text-pine" />
              {event.plats}
            </span>
          )}
          {event.arrangor && (
            <span className="flex items-center gap-2">
              <Users size={18} className="text-pine" />
              {event.arrangor}
            </span>
          )}
          {(event.autor || event.autopilot?.autor) && (
            <span data-testid="event-autor" className="flex items-center gap-2">
              <User size={18} className="text-pine" />
              {event.autor || event.autopilot?.autor}
            </span>
          )}
        </div>

        {event.kortbeskrivning && (
          <p className="text-base italic text-bark/80 mb-3 leading-relaxed border-l-2 border-falu/40 pl-3">
            {event.kortbeskrivning}
          </p>
        )}
        {event.beskrivning && event.beskrivning !== event.kortbeskrivning && (
          <ExpandableBeskrivning
            full={event.beskrivning}
            threshold={400}
            testId={`event-${event.id}-besk`}
          />
        )}
      </div>

      {/* Etappe 4.11 — Galleri (bilder/video/ljud) i samma ordning som modview */}
      {event.galleri && event.galleri.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <GalleriStrip bilder={event.galleri} galleriAudio={event.galleri_audio} alt={event.titel} />
        </div>
      )}

      {(event.kontakt_epost || event.kontakt_telefon || event.kontakt) && (
        <div className="bg-bone-alt rounded-sm p-6 mb-6">
          <h2 className="font-heading text-lg font-medium text-ink mb-3">Kontakt</h2>
          <div className="flex flex-col gap-2">
            {event.kontakt_epost && (
              <a href={`mailto:${event.kontakt_epost}`} className="inline-flex items-center gap-2 text-base text-falu hover:text-falu-hover no-underline">
                <Mail size={18} /> {event.kontakt_epost}
              </a>
            )}
            {event.kontakt_telefon && (
              <a href={`tel:${event.kontakt_telefon.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 text-base text-falu hover:text-falu-hover no-underline">
                <Phone size={18} /> {event.kontakt_telefon}
              </a>
            )}
          </div>
        </div>
      )}

      <div data-testid="event-trust-notice" className="bg-bone-alt/60 border-l-3 border-falu/60 rounded-sm p-4 mb-6 flex gap-3 items-start">
        <AlertTriangle size={18} className="text-falu shrink-0 mt-0.5" />
        <div className="text-sm text-bark leading-relaxed flex-1 min-w-0">
          <p>
            <strong>Uppgifter kan ändras.</strong> Kontrollera alltid tid, plats och kontaktuppgifter hos arrangören innan du åker.
          </p>
          {event.auto_imported && (event.source_name || event.source_url) && (
            <div data-testid="event-source-info" className="mt-2 pt-2 border-t border-warm-border/50 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-bark/85">
              <span className="inline-flex items-center gap-1.5">
                <Database size={12} className="text-pine" />
                Källa:{" "}
                {event.source_url ? (
                  <a
                    href={event.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-0.5 text-falu hover:text-falu-hover underline decoration-dotted"
                  >
                    {event.source_name || event.source_url}
                    <ExternalLink size={10} />
                  </a>
                ) : (
                  <span className="font-medium">{event.source_name}</span>
                )}
              </span>
              {event.last_checked_at && (
                <span className="text-muted-foreground">
                  Senast kontrollerad:{" "}
                  {new Date(event.last_checked_at).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" })}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Etappe 1.6 — standardiserade sociala/media-länkar (webbplats, FB, IG, YouTube + media_url backwards-compat) */}
      <SocialLinks
        webbplats={event.webbplats}
        facebook_url={event.facebook_url}
        instagram_url={event.instagram_url}
        youtube_url={event.youtube_url}
        media_url={event.media_url}
        media_typ={event.media_typ}
        media_beskrivning={event.media_beskrivning}
      />

      {/* Etappe 4.11 — Enhetlig footnote (Tags · Källa · Senast uppdaterad). Visas endast om något finns. */}
      <EntryFootnote
        taggar={event.taggar}
        kalla={event.kalla || event.source_name}
        senast={event.senast_andrad || event.publiceringsdatum || event.inskicksdatum || event.last_checked_at}
        uppdaterad_av={event.andrad_av}
        testIdPrefix={`event-${event.id}`}
      />

      <div className="mt-4 pt-4 border-t border-warm-border/60 flex flex-wrap items-center gap-3">
        <ShareWidget
          title={event.titel}
          text={event.beskrivning || event.plats || ""}
          path={`/evenemang/${event.id}`}
        />
        <AndringsforslagButton typ="evenemang" referensId={event.id} referensNamn={event.titel} />
      </div>
    </div>
  );
}
